import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useAuth } from '../contexts/AuthContext';
import { useAnalyticsConsent } from '../contexts/AnalyticsContext';
import { GA_MEASUREMENT_ID, IS_ANALYTICS_ENABLED, gaConfig } from './config';

/**
 * Google Analytics Component
 *
 * Handles GA4 initialization and automatic page view tracking.
 * Only tracks when user has given consent.
 */
export const GoogleAnalytics: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { hasConsent } = useAnalyticsConsent();

  // Initialize GA4 once when consent is given
  useEffect(() => {
    if (!IS_ANALYTICS_ENABLED || !GA_MEASUREMENT_ID || !hasConsent) {
      return;
    }

    try {
      ReactGA.initialize(GA_MEASUREMENT_ID, {
        gaOptions: gaConfig,
      });

      // Set user properties (non-PII only)
      if (user) {
        ReactGA.set({
          user_role: user.role || 'regular',
          organization_id: user.organizationId || 'unknown',
          user_type: user.email?.includes('@tester.') ? 'tester' : 'regular',
        });
      }
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }, [hasConsent, user]);

  // Track page views on route change
  useEffect(() => {
    if (!IS_ANALYTICS_ENABLED || !hasConsent) {
      return;
    }

    try {
      const page = location.pathname + location.search;
      ReactGA.send({
        hitType: 'pageview',
        page,
        title: document.title,
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }, [location, hasConsent]);

  return null; // This component only handles side effects
};
